import { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { NavLink, matchPath, useLocation } from 'react-router-dom';
import { hasRoleAccess } from '../../auth/roles';
import {
  NAV_ITEM_ICONS,
  NavSectionAdminIcon,
  NavSectionBusinessIcon,
} from '../icons/navIcons';
import { LockedFeatureBadge } from '../../../components/entitlements/LockedFeatureBadge';
import styles from './RoleNavigationShell.module.css';

/** Sidebar link target: strip trailing `/*` from React Router splat patterns (e.g. `/integrations/*`). */
const navLinkTo = (path) => path.replace(/\/?\*$/, '');

/** Tablet band where sidebar collapses to an icon strip (matches CSS). */
const TABLET_SIDEBAR_MEDIA = '(min-width: 768px) and (max-width: 1199px)';

/** Two activations on the same item within this window toggle the rail (touch-friendly; `click.detail` is unreliable on touch). */
const TABLET_SIDEBAR_DOUBLE_TAP_MS = 480;

export const RoleNavigationShell = ({ role, modules, t, renderHeader, children }) => {
  const location = useLocation();
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isTabletSidebarRange, setIsTabletSidebarRange] = useState(false);
  /** When true at tablet widths only, nav expands to full label layout. */
  const [tabletNavExpanded, setTabletNavExpanded] = useState(false);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const mql768 = window.matchMedia('(min-width: 768px)');
    const handler = () => {
      if (mql768.matches) setMobileMenuOpen(false);
    };
    mql768.addEventListener('change', handler);
    return () => mql768.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    const mqlTablet = window.matchMedia(TABLET_SIDEBAR_MEDIA);
    const handler = () => {
      setIsTabletSidebarRange(mqlTablet.matches);
    };
    handler();
    mqlTablet.addEventListener('change', handler);
    return () => mqlTablet.removeEventListener('change', handler);
  }, []);

  const allowedModules = useMemo(
    () => modules.filter((module) => hasRoleAccess(role, module.requiredRole)),
    [modules, role],
  );
  const groupedModules = useMemo(
    () => ({
      business: allowedModules.filter((module) => module.section === 'business'),
      admin: allowedModules.filter((module) => module.section === 'admin'),
    }),
    [allowedModules],
  );
  const activeModule = useMemo(
    () => resolveActiveModule(allowedModules, location.pathname),
    [allowedModules, location.pathname],
  );

  const tabletSidebarTapClearRef = useRef(null);

  const tabletSidebarTapRef = useRef({ ts: null, moduleId: null });

  useEffect(
    () => () => {
      if (tabletSidebarTapClearRef.current) {
        clearTimeout(tabletSidebarTapClearRef.current);
        tabletSidebarTapClearRef.current = null;
      }
    },
    [],
  );

  useEffect(() => {
    if (!isTabletSidebarRange) {
      tabletSidebarTapRef.current = { ts: null, moduleId: null };
      if (tabletSidebarTapClearRef.current) {
        clearTimeout(tabletSidebarTapClearRef.current);
        tabletSidebarTapClearRef.current = null;
      }
    }
  }, [isTabletSidebarRange]);

  const toggleTabletNav = useCallback(() => {
    setTabletNavExpanded((prev) => !prev);
  }, []);

  /**
   * Tablet (768–1199): two quick taps/clicks on the same nav link toggle the rail (`click.detail`
   * is unreliable on browsers that synthesize click from touch).
   */
  const handleTabletNavLinkClick = useCallback(
    (e, moduleId) => {
      if (!isTabletSidebarRange) return;
      const id = String(moduleId);
      const now = Date.now();
      const { ts, moduleId: priorId } = tabletSidebarTapRef.current;

      const doubleTapSameItem =
        priorId !== null &&
        ts !== null &&
        priorId === id &&
        now - ts < TABLET_SIDEBAR_DOUBLE_TAP_MS;

      if (!doubleTapSameItem) {
        tabletSidebarTapRef.current = { ts: now, moduleId: id };
        if (tabletSidebarTapClearRef.current) {
          clearTimeout(tabletSidebarTapClearRef.current);
        }
        tabletSidebarTapClearRef.current = setTimeout(() => {
          tabletSidebarTapRef.current = { ts: null, moduleId: null };
          tabletSidebarTapClearRef.current = null;
        }, TABLET_SIDEBAR_DOUBLE_TAP_MS);
        return;
      }

      e.preventDefault();
      if (tabletSidebarTapClearRef.current) {
        clearTimeout(tabletSidebarTapClearRef.current);
        tabletSidebarTapClearRef.current = null;
      }
      tabletSidebarTapRef.current = { ts: null, moduleId: null };
      toggleTabletNav();
    },
    [isTabletSidebarRange, toggleTabletNav],
  );

  const renderNavItems = useCallback(
    ({ items }) =>
      items
        .filter((module) => module.nav !== false)
        .map((module) => {
          const Icon = NAV_ITEM_ICONS[module.id];
          const isLocked = Boolean(module.locked);
          const className = ({ isActive }) =>
            [
              styles.item,
              isActive && !isLocked ? styles.itemActive : '',
              isLocked ? styles.itemLocked : '',
            ]
              .filter(Boolean)
              .join(' ');
          const label = (
            <>
              {Icon ? (
                <span className={styles.itemIcon}>
                  <Icon size={20} />
                </span>
              ) : null}
              <span className={styles.itemLabel}>{t(module.labelKey)}</span>
              {isLocked ? <LockedFeatureBadge t={t} className={styles.itemBadge} /> : null}
            </>
          );

          if (isLocked) {
            return (
              <span
                key={module.id}
                className={className({ isActive: false })}
                title={t('entitlementsLockedNavHint')}
                aria-disabled="true"
              >
                {label}
              </span>
            );
          }

          return (
            <NavLink
              key={module.id}
              className={className}
              to={navLinkTo(module.path)}
              title={t(module.labelKey)}
              onClick={(e) => handleTabletNavLinkClick(e, module.id)}
            >
              {label}
            </NavLink>
          );
        }),
    [handleTabletNavLinkClick, t],
  );

  const navClassName = `${styles.nav} ${tabletNavExpanded ? styles.navSidebarExpanded : ''}`.trim();

  const headerContent = renderHeader
    ? renderHeader({
        activeModule,
        onMenuToggle: () => setMobileMenuOpen((prev) => !prev),
        isMobileMenuOpen,
      })
    : null;

  return (
    <section
      className={`${styles.navShell} ${isMobileMenuOpen ? styles.navShellOpen : ''}`.trim()}
    >
      {isMobileMenuOpen ? (
        <button
          type="button"
          className={styles.overlay}
          aria-label={t('closeMenu')}
          onClick={() => setMobileMenuOpen(false)}
        />
      ) : null}
      <nav className={navClassName} aria-label={t('navAriaLabel')}>
        <div className={styles.sidebarChrome}>
          <div id="sidebar-main-groups" className={styles.sidebarBody}>
            <div className={styles.section}>
              <span className={styles.sectionTitle}>
                <span className={styles.sectionTitleIcon} aria-hidden="true">
                  <NavSectionBusinessIcon size={18} />
                </span>
                <span className={styles.sectionTitleText}>{t('navSectionBusiness')}</span>
              </span>
              {renderNavItems({ items: groupedModules.business })}
            </div>
            {groupedModules.admin.length ? (
              <div className={`${styles.section} ${styles.sectionAdmin}`}>
                <span className={styles.divider} />
                <span className={styles.sectionTitle}>
                  <span className={styles.sectionTitleIcon} aria-hidden="true">
                    <NavSectionAdminIcon size={18} />
                  </span>
                  <span className={styles.sectionTitleText}>{t('navSectionAdmin')}</span>
                </span>
                {renderNavItems({ items: groupedModules.admin })}
              </div>
            ) : null}
          </div>
        </div>
      </nav>
      <div className={styles.main}>
        <div className={styles.content}>
          {headerContent}
          {children}
        </div>
      </div>
    </section>
  );
};

const resolveActiveModule = (modules, pathname) => {
  const matchedModules = modules.filter((module) =>
    matchPath({ path: module.path, end: false }, pathname),
  );
  if (!matchedModules.length) {
    return null;
  }
  return matchedModules.sort((first, second) => second.path.length - first.path.length)[0];
};
